import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import moment from 'moment'

import i18n from '~/i18n'
import axios from '~/data/http/axios'
import { alterFullpage, alterSiteDrawer } from '~/data/actions/siteActions'
import { updateAccountProgress } from '~/data/actions/accountActions'

import Rum from '~/Classes/rum'

import ButtonNew from '~/Common-Objects/UI/Button'
import Column from '~/Common-Objects/Grids/Column/Column'
import Row from '~/Common-Objects/Grids/Row/Row'

import KnowledgeBaseCallout from '~/Common-Objects/Callout/knowledgeBase'
import SalesCallout from '~/Common-Objects/Callout/sales'
import CustomerSuccessCallout from '~/Common-Objects/Callout/customerSuccess'

import {
    OnboardingSetup,
    OnboardingDescription,
    OnboardingHeader,
    OnboardingTitle,
    OnboardingContent,
    OnboardingSteps,
    OnboardingStep,
    OnboardingSplash,
    OnboardingContainer,
    OnboardingFooter
} from '~/Common-Objects/Onboarding'
import {
    ImportStep,
    BrandStep,
    TemplateStep,
    AutomationStep,
    UsersStep,
    DeliverabilityStep,
    CampaignStep
} from '~/Common-Objects/Onboarding/steps'
import SupportCallToAction from '~/Common-Objects/SupportCallToAction'
import Mailchimp2ImportCta from '~/Common-Objects/Mailchimp2ImportCta'
import { isOnboardingInProgress } from '~/utils/onboardingProgress'
import VideoModal from '~/Common-Objects/VideoModal'

import CreateAutomationModal from '~/pages/cp/includes/automations/createAutomationModal'
import CreateSubSelectGroup from '~/pages/cp/includes/bb_topNav/createSubSelectGroup'
import CreateCampaign from '../campaigns/add/drawerAddCampaignStep1'
import CreateTemplate from '../templates/add/templatesAdd'

import InviteStaffModal from '../company/staff/inviteStaffModal'

import onboardingPoster from '~/assets/images/setup/video-poster-large.png'

import './index.scss'

export default function Setup() {
    const dispatch = useDispatch()
    const history = useHistory()
    const site = useSelector((state) => state.siteMaster)
    const account = useSelector((state) => state.accountMaster)

    const [hasMailchimp, setHasMailchimp] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(null)
    const [showCallout, setShowCallout] = useState(account?.progress?.showOnboardingVideo)

    const showSetup = isOnboardingInProgress(account, site?.siteId)

    useEffect(() => {
        axios.get('/accountMaster/hasMailchimp').then((res) => {
            setHasMailchimp(Boolean(res.data.hasMailchimp))
        })
    }, [])

    const steps = [
        {
            Component: ImportStep,
            isComplete: account.progress.import,
            props: {
                onImport: () => dispatch(alterSiteDrawer(true, true, 'right', <CreateSubSelectGroup />, true, 500)),
                postHogTag: 'onboarding-list-button-import-contacts'
            }
        },
        {
            Component: BrandStep,
            isComplete: account.progress.brand,
            props: {
                onAnalyze: () => history.push('/cp/brandanalyzer'),
                postHogTag: 'onboarding-list-button-brand-analyzer'
            }
        },
        {
            Component: TemplateStep,
            isComplete: account.progress.template,
            props: {
                onCreateTemplate: () =>
                    dispatch(alterSiteDrawer(true, true, 'right', <CreateTemplate history={history} />, true, '100vw')),
                postHogTag: 'onboarding-list-button-create-template'
            }
        },
        {
            Component: CampaignStep,
            isComplete: account.progress.campaign,
            props: {
                onCreateCampaign: () =>
                    dispatch(
                        alterSiteDrawer(
                            true,
                            false,
                            'right',
                            <CreateCampaign history={history} />,
                            true,
                            500,
                            'drawer_nopad'
                        )
                    ),
                postHogTag: 'onboarding-list-button-create-campaign'
            }
        },
        {
            Component: AutomationStep,
            isComplete: account.progress.automation,
            props: {
                onCreateAutomation: () =>
                    dispatch(alterFullpage(<CreateAutomationModal onClose={() => dispatch(alterFullpage(null))} />)),
                postHogTag: 'onboarding-list-button-explore-automations'
            }
        },
        {
            Component: UsersStep,
            isComplete: account.progress.users,
            props: {
                canSkip: Object.entries(account.progress)
                    .filter(([key]) => key !== 'users')
                    .every(([, value]) => value === true),
                onInvite: () =>
                    dispatch(alterFullpage(<InviteStaffModal onClose={() => dispatch(alterFullpage(null))} />)),
                onSkip: handleHideSetupSteps,
                postHogTag: 'onboarding-list-button-invite',
                postHogTagSkip: 'onboarding-list-button-invite-skip'
            }
        },
        {
            Component: DeliverabilityStep,
            isComplete: account.progress.deliverability,
            props: {
                onOptimize: () => history.push('/cp/company/domains'),
                postHogTag: 'onboarding-list-button-authenticate-domain'
            }
        }
    ]

    const firstIncompleteIndex = steps.findIndex((step) => !step.isComplete)

    const handleHideSetupSteps = async () => {
        Rum.trackEvent('getting_started_complete')
        await axios.put('/accountMaster/onboarding')
        dispatch(updateAccountProgress())
    }

    const handleHideCallout = async () => {
        setShowCallout(false)
        await axios.put('/accountMaster/onboarding/video')
    }

    const handleStepClick = (index) => {
        if (selectedIndex === index) return
        setSelectedIndex(index)
    }

    const showVideoModal = () => {
        dispatch(
            alterFullpage(
                <VideoModal
                    video='https://cdn1.ourmailsender.com/siteContent/videos/new-customer-orientation-no-call.mp4'
                    poster={onboardingPoster}
                    height={562}
                    width={1000}
                    autoPlay={true}
                    onClose={() => dispatch(alterFullpage(null))}
                />
            )
        )
    }

    const showSuccessVideoModal = () => {
        dispatch(
            alterFullpage(
                <VideoModal
                    video='https://cdn1.ourmailsender.com/siteContent/videos/new-customer-orientation-call.mp4'
                    poster={onboardingPoster}
                    height={562}
                    width={1000}
                    autoPlay={true}
                    onClose={() => dispatch(alterFullpage(null))}
                />
            )
        )
    }

    const OnboardingCallout = () => {
        const capsuleCreated =
            account.accountMaster.options.capsuleCreatedOn && moment(account.accountMaster.options.capsuleCreatedOn)

        const totalSubscribers = account.accountMaster.totalSubscribers || 0
        const subscriberCondition = totalSubscribers >= 5000

        const threeMonthsAgo = moment().subtract(3, 'months')
        const showCustomerSuccessCallout =
            capsuleCreated && moment(capsuleCreated).isBefore(threeMonthsAgo) && subscriberCondition
        const showSalesCallout = capsuleCreated && moment(capsuleCreated).isAfter(threeMonthsAgo) && subscriberCondition

        if (showCustomerSuccessCallout) {
            return <CustomerSuccessCallout onPosterClick={showSuccessVideoModal} onClose={handleHideCallout} />
        } else if (showSalesCallout) {
            return <SalesCallout onPosterClick={showSuccessVideoModal} onClose={handleHideCallout} />
        }

        return <KnowledgeBaseCallout onPosterClick={showVideoModal} onClose={handleHideCallout} />
    }

    useEffect(() => {
        if (!showSetup) {
            history.push('/cp/home')
        }
    }, [showSetup])

    useEffect(() => {
        let intervalId

        if (showSetup) {
            dispatch(updateAccountProgress())

            intervalId = setInterval(() => {
                dispatch(updateAccountProgress())
            }, 5000)
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId)
            }
        }
    }, [showSetup, dispatch])

    return (
        <OnboardingContainer>
            {showCallout && <OnboardingCallout />}
            <OnboardingSetup>
                <OnboardingHeader>
                    <OnboardingTitle>{i18n.t('setup:title', { siteName: site.siteName })}</OnboardingTitle>
                    <OnboardingDescription>{i18n.t('setup:content')}</OnboardingDescription>
                </OnboardingHeader>
                <OnboardingContent>
                    <OnboardingSteps>
                        <OnboardingStep title={i18n.t('setup:steps.create-account')} isComplete={true} />
                        {steps.map((step, index) => {
                            const StepComponent = step.Component
                            return (
                                <StepComponent
                                    isOpen={
                                        selectedIndex !== null
                                            ? selectedIndex === index
                                            : index === firstIncompleteIndex
                                    }
                                    isComplete={step.isComplete}
                                    onClick={() => handleStepClick(index)}
                                    {...step.props}
                                />
                            )
                        })}
                    </OnboardingSteps>
                    <OnboardingSplash>
                        {steps[selectedIndex !== null ? selectedIndex : firstIncompleteIndex]?.Component.splash}
                    </OnboardingSplash>
                </OnboardingContent>
            </OnboardingSetup>
            <Row>
                {hasMailchimp && (
                    <Column xs={12}>
                        <Mailchimp2ImportCta />
                    </Column>
                )}
                <Column xs={12} margin={0}>
                    <SupportCallToAction
                        emailUsPostHogTag={'onboarding-list-button-email-us'}
                        supportDocsPostHogTag={'onboarding-list-button-support-docs'}
                    />
                </Column>
            </Row>

            <OnboardingFooter>
                <ButtonNew
                    variant='tertiary'
                    size='s'
                    onClick={handleHideSetupSteps}
                    data-ph='onboarding-list-button-remove-setup'
                    style={{ boxShadow: 'none' }}
                >
                    {i18n.t('setup:remove-setup-steps')}
                </ButtonNew>
            </OnboardingFooter>
        </OnboardingContainer>
    )
}
